// Apply a decorative combo (from src/data/decorative.ts) to a user's own text.
//
// Two shapes of combo:
//   - Two-line borders ("top\nbottom"): the text drops onto a centered middle
//     line inside the frame → top / your text / bottom.
//   - Single-line flourishes ("─── ⋆⋅☆⋅⋆ ───"): the ornament wraps the text on
//     both sides, mirrored so directional glyphs (brackets, arrows) face inward.
//
// With no text, the combo is returned unchanged — so the gallery still shows the
// ready-made decorations to copy (and SSR/SEO output is identical to before).

/** Glyphs that have a left/right mirror image, so a wrapped right side faces in. */
const MIRROR: Record<string, string> = {
  "(": ")", ")": "(",
  "[": "]", "]": "[",
  "{": "}", "}": "{",
  "<": ">", ">": "<",
  "«": "»", "»": "«",
  "‹": "›", "›": "‹",
  "❮": "❯", "❯": "❮",
  "❬": "❭", "❭": "❬",
  "「": "」", "」": "「",
  "『": "』", "』": "『",
  "〔": "〕", "〕": "〔",
  "〖": "〗", "〗": "〖",
  "〘": "〙", "〙": "〘",
  "【": "】", "】": "【",
  "❨": "❩", "❩": "❨",
  "❪": "❫", "❫": "❪",
  "❰": "❱", "❱": "❰",
  "❲": "❳", "❳": "❲",
  "❴": "❵", "❵": "❴",
  "⟨": "⟩", "⟩": "⟨",
  "⟦": "⟧", "⟧": "⟦",
  "⌈": "⌉", "⌉": "⌈",
  "⌊": "⌋", "⌋": "⌊",
  "◁": "▷", "▷": "◁",
  "◀": "▶", "▶": "◀",
  "◂": "▸", "▸": "◂",
  "⊰": "⊱", "⊱": "⊰",
  "ʚ": "ɞ", "ɞ": "ʚ",
  "꒰": "꒱", "꒱": "꒰",
  "୨": "୧", "୧": "୨",
  "༺": "༻", "༻": "༺",
  "꧁": "꧂", "꧂": "꧁",
  "/": "\\", "\\": "/",
};

/** Reverse a string and swap each mirrorable glyph for its counterpart. */
export function mirrorString(s: string): string {
  let out = "";
  for (const ch of s) out = (MIRROR[ch] ?? ch) + out;
  return out;
}

/** Wrap `text` in the decorative `combo`; returns `combo` unchanged when `text` is blank.
 *  `mirror` flips/swaps the right side so frame glyphs face inward (borders,
 *  brackets). Faces must NOT be mirrored — a reversed kaomoji is broken — so they
 *  pass `mirror: false` and get the face unchanged on both sides. */
export function applyDecoration(combo: string, text: string, mirror = true): string {
  const t = text.trim();
  if (!t) return combo;

  if (combo.includes("\n")) {
    const lines = combo.split("\n");
    const top = lines[0];
    const bottom = lines[lines.length - 1];
    const width = Math.max([...top].length, [...bottom].length);
    const pad = Math.max(0, width - [...t].length);
    const left = Math.floor(pad / 2);
    const mid = " ".repeat(left) + t + " ".repeat(pad - left);
    return `${top}\n${mid}\n${bottom}`;
  }

  return `${combo} ${t} ${mirror ? mirrorString(combo) : combo}`;
}
